import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  vehicle: "",
  package: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") delete payload[k];
      });
      await axios.post(`${API}/contact`, payload);
      toast.success("Inquiry sent. We'll be in touch within a few hours.");
      setForm(initialForm);
    } catch (err) {
      const msg =
        err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#070707] border-t border-zinc-900"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-12 h-px bg-red-500" />
            <span className="text-xs tracking-[0.3em] uppercase text-red-500 font-bold">
              Get in Touch
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter leading-[0.95]">
            Request a <br />
            <span className="text-red-500 italic font-serif tracking-normal lowercase">
              quote
            </span>{" "}
            today.
          </h2>
          <p className="mt-8 text-zinc-400 text-base md:text-lg leading-relaxed max-w-md">
            Tell us about your vehicle. We'll confirm availability and send a
            personalized quote within hours.
          </p>

          <div className="mt-12 space-y-6">
            <a
              href="tel:+19206278405"
              data-testid="contact-phone"
              className="flex items-start gap-4 group"
            >
              <span className="w-11 h-11 border border-zinc-800 group-hover:border-red-500 flex items-center justify-center transition-colors">
                <Phone size={16} className="text-red-500" />
              </span>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-zinc-500">
                  Call
                </div>
                <div className="font-display text-lg tracking-tight mt-1 group-hover:text-red-500 transition-colors">
                  920 627 8405
                </div>
              </div>
            </a>

            <a
              href="mailto:roystonmobiledetailing@gmail.com"
              data-testid="contact-email"
              className="flex items-start gap-4 group"
            >
              <span className="w-11 h-11 border border-zinc-800 group-hover:border-red-500 flex items-center justify-center transition-colors">
                <Mail size={16} className="text-red-500" />
              </span>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-zinc-500">
                  Email
                </div>
                <div className="font-display text-lg tracking-tight mt-1 group-hover:text-red-500 transition-colors">
                  roystonmobiledetailing@gmail.com
                </div>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <span className="w-11 h-11 border border-zinc-800 flex items-center justify-center">
                <MapPin size={16} className="text-red-500" />
              </span>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-zinc-500">
                  Studio
                </div>
                <div className="font-display text-lg tracking-tight mt-1">
                  812 N 8th St, Sheboygan, WI
                </div>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <span className="w-11 h-11 border border-zinc-800 flex items-center justify-center">
                <Clock size={16} className="text-red-500" />
              </span>
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-zinc-500">
                  Hours
                </div>
                <div className="font-display text-lg tracking-tight mt-1">
                  Mon–Sat · 8am – 6pm
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={handleSubmit}
            className="bg-[#0a0a0a] border border-zinc-900 p-8 md:p-12 space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2 block">
                  Name *
                </label>
                <Input
                  name="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="bg-[#050505] border-zinc-800 rounded-none h-12 text-white focus-visible:ring-red-500 focus-visible:ring-1 focus-visible:ring-offset-0"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2 block">
                  Email *
                </label>
                <Input
                  type="email"
                  name="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="roystonmobiledetailing@gmail.com"
                  className="bg-[#050505] border-zinc-800 rounded-none h-12 text-white focus-visible:ring-red-500 focus-visible:ring-1 focus-visible:ring-offset-0"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2 block">
                  Phone
                </label>
                <Input
                  name="phone"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="920 627 8405"
                  className="bg-[#050505] border-zinc-800 rounded-none h-12 text-white focus-visible:ring-red-500 focus-visible:ring-1 focus-visible:ring-offset-0"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2 block">
                  Vehicle (make / model)
                </label>
                <Input
                  name="vehicle"
                  data-testid="contact-input-vehicle"
                  value={form.vehicle}
                  onChange={handleChange}
                  placeholder="2022 BMW M3"
                  className="bg-[#050505] border-zinc-800 rounded-none h-12 text-white focus-visible:ring-red-500 focus-visible:ring-1 focus-visible:ring-offset-0"
                />
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2 block">
                Interested in
              </label>
              <Select
                value={form.package}
                onValueChange={(v) => setForm((f) => ({ ...f, package: v }))}
              >
                <SelectTrigger
                  data-testid="contact-select-package"
                  className="bg-[#050505] border-zinc-800 rounded-none h-12 text-white focus:ring-red-500 focus:ring-1 focus:ring-offset-0"
                >
                  <SelectValue placeholder="Select a package or service" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-zinc-800 text-white rounded-none">
                  <SelectItem value="Basic Package">Basic Package</SelectItem>
                  <SelectItem value="Premium Package">Premium Package</SelectItem>
                  <SelectItem value="Ultimate Package">Ultimate Package</SelectItem>
                  <SelectItem value="Exterior Only">Exterior Only</SelectItem>
                  <SelectItem value="Interior Only">Interior Only</SelectItem>
                  <SelectItem value="Ceramic Coating">Ceramic Coating</SelectItem>
                  <SelectItem value="Paint Correction">Paint Correction</SelectItem>
                  <SelectItem value="Not Sure Yet">Not Sure Yet</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2 block">
                Message *
              </label>
              <Textarea
                name="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell us about your vehicle's condition, preferred dates, and any questions..."
                rows={5}
                className="bg-[#050505] border-zinc-800 rounded-none text-white focus-visible:ring-red-500 focus-visible:ring-1 focus-visible:ring-offset-0 resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="w-full inline-flex items-center justify-center gap-3 bg-red-500 hover:bg-red-400 disabled:bg-red-500/40 disabled:cursor-not-allowed text-white px-8 py-5 font-bold text-xs tracking-[0.25em] uppercase transition-all duration-300"
            >
              {loading ? "Sending..." : "Send Inquiry"} <Send size={14} />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
